import { evaluationGridTemplateService } from '@/services/evaluation-grid-templates.service';
import { sortEvaluationGridFields } from '@/util/evaluation-grid-util';
import axios from 'axios';

const initialState = () => ({
  evaluationGridTemplates: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getEvaluationGridTemplates({ commit }) {
    commit('GET_EVALUATION_GRID_TEMPLATES');
    return evaluationGridTemplateService.getEvaluationGridTemplates().then(
      (evaluationGridTemplates) => {
        commit('GET_EVALUATION_GRID_TEMPLATES_SUCCESS', evaluationGridTemplates);
        return evaluationGridTemplates;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('GET_EVALUATION_GRID_TEMPLATES_ERROR', error);
          throw error;
        }
      },
    );
  },
  addEvaluationGridTemplate({ commit }, { name, isPrimary }) {
    commit('ADD_EVALUATION_GRID_TEMPLATE');
    return evaluationGridTemplateService.addTemplate({ name, isPrimary }).then(
      (data) => {
        commit('ADD_EVALUATION_GRID_TEMPLATE_SUCCESS', data);
        return data;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ADD_EVALUATION_GRID_TEMPLATE_ERROR', error);
          throw error;
        }
      },
    );
  },
  deleteEvaluationGridTemplate({ commit }, templateId) {
    commit('DELETE_EVALUATION_GRID_TEMPLATE');
    return evaluationGridTemplateService.deleteTemplate(templateId).then(
      (data) => {
        commit('DELETE_EVALUATION_GRID_TEMPLATE_SUCCESS', templateId);
        return data;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('DELETE_EVALUATION_GRID_TEMPLATE_ERROR', error);
          throw error;
        }
      },
    );
  },
  editEvaluationGridTemplate({ commit }, { templateId, templateData }) {
    commit('EDIT_EVALUATION_GRID_TEMPLATE');
    return evaluationGridTemplateService.editTemplate(templateId, templateData).then(
      (data) => {
        commit('EDIT_EVALUATION_GRID_TEMPLATE_SUCCESS', data);
        return data;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('EDIT_EVALUATION_GRID_TEMPLATE_ERROR', error);
          throw error;
        }
      },
    );
  },
  _updateEvaluationGridTemplateField({ commit }, { evaluationGridTemplateId, updatedField }) {
    const template = state.evaluationGridTemplates.find((t) => t.id === evaluationGridTemplateId);
    if (template) {
      const fieldIndex = template.fields.findIndex((field) => field.id === updatedField.id);
      if (fieldIndex !== -1) {
        template.fields.splice(fieldIndex, 1, updatedField);
      }
    }
    state.evaluationGridTemplates = state.evaluationGridTemplates.map((template) => sortEvaluationGridFields(template));
  },
  _deleteEvaluationGridTemplateFields({ commit }, { evaluationGridTemplateId, fieldIdToDelete }) {
    const template = state.evaluationGridTemplates.find((t) => t.id === evaluationGridTemplateId);
    if (template) {
      template.fields = template.fields.filter((field) => field.id !== fieldIdToDelete);
    }
  },
};

const mutations = {
  GET_EVALUATION_GRID_TEMPLATES(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    // Keep existing templates while refreshing to avoid UI flash (DocumentsSummary
    // would otherwise blank the evaluation grid then re-render it).
  },
  GET_EVALUATION_GRID_TEMPLATES_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  GET_EVALUATION_GRID_TEMPLATES_SUCCESS(state, evaluationGridTemplates) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.evaluationGridTemplates = (evaluationGridTemplates || []).map((template) =>
      sortEvaluationGridFields(template),
    );
  },
  ADD_EVALUATION_GRID_TEMPLATE(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  ADD_EVALUATION_GRID_TEMPLATE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  ADD_EVALUATION_GRID_TEMPLATE_SUCCESS(state, data) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.evaluationGridTemplates.push(data);
  },
  DELETE_EVALUATION_GRID_TEMPLATE(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_EVALUATION_GRID_TEMPLATE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  DELETE_EVALUATION_GRID_TEMPLATE_SUCCESS(state, templateId) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.evaluationGridTemplate = state.evaluationGridTemplates.filter((template) => template.id !== templateId);
  },
  EDIT_EVALUATION_GRID_TEMPLATE(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  EDIT_EVALUATION_GRID_TEMPLATE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  EDIT_EVALUATION_GRID_TEMPLATE_SUCCESS(state, updatedTemplate) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.evaluationGridTemplates.findIndex((template) => template.id === updatedTemplate.id);
    if (index !== -1) {
      state.evaluationGridTemplates.splice(index, 1, updatedTemplate);
    }
  },
};

const getters = {
  evaluationGridTemplates: (state) => state.evaluationGridTemplates,
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const evaluationGridTemplates = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
